import type { TLRichText } from "@tldraw/tlschema";

type Node = {
  type: string;
  text?: string;
  marks?: { type: string; [key: string]: unknown }[];
  content?: Node[];
  attrs?: Record<string, unknown>;
};
export function richTextPlain(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const n = value as Node;
  if (typeof n.text === "string") return n.text;
  if (n.type === "hardBreak") return "\n";
  if (!n.content && /mention/i.test(n.type))
    return `@${n.attrs?.label ?? n.attrs?.name ?? n.attrs?.id ?? "mention"}`;
  return (n.content ?? [])
    .map(richTextPlain)
    .join(n.type === "doc" || n.type === "bulletList" ? "\n" : "");
}
export function preserveTextStyle(
  current: TLRichText,
  next: TLRichText,
  overrideBold = false,
): TLRichText {
  const leaves: Node[] = [];
  const visit = (n: Node) => {
    if (n.text) leaves.push(n);
    n.content?.forEach(visit);
  };
  visit(current as Node);
  const common = (leaves[0]?.marks ?? []).filter(
    (mark) =>
      [
        "bold",
        "italic",
        "underline",
        "strike",
        "textStyle",
        "highlight",
      ].includes(mark.type) &&
      (!overrideBold || mark.type !== "bold") &&
      leaves.every((n) =>
        n.marks?.some((m) => JSON.stringify(m) === JSON.stringify(mark)),
      ),
  );
  const copy = structuredClone(next) as Node;
  const apply = (n: Node) => {
    if (n.text)
      n.marks = [
        ...common,
        ...(n.marks ?? []).filter(
          (m) => !common.some((c) => c.type === m.type),
        ),
      ];
    n.content?.forEach(apply);
  };
  apply(copy);
  return copy as TLRichText;
}
export function editTextRange(
  value: TLRichText,
  start: number,
  end: number,
  text: string,
): TLRichText {
  const copy = structuredClone(value) as Node,
    spans: { node: Node; start: number; end: number; atom: boolean }[] = [];
  let offset = 0;
  const visit = (n: Node) => {
    if (n.text !== undefined) {
      spans.push({
        node: n,
        start: offset,
        end: offset + n.text.length,
        atom: false,
      });
      offset += n.text.length;
      return;
    }
    if (n.type === "hardBreak" || (!n.content && /mention/i.test(n.type))) {
      const len = richTextPlain(n).length;
      spans.push({ node: n, start: offset, end: offset + len, atom: true });
      offset += len;
      return;
    }
    n.content?.forEach((child, i) => {
      if (i && (n.type === "doc" || n.type === "bulletList")) {
        spans.push({ node: n, start: offset, end: offset + 1, atom: true });
        offset++;
      }
      visit(child);
    });
  };
  visit(copy);
  const plain = richTextPlain(copy);
  const split = (n: number) =>
    n > 0 &&
    n < plain.length &&
    /[\uD800-\uDBFF]/.test(plain[n - 1]) &&
    /[\uDC00-\uDFFF]/.test(plain[n]);
  if (start > end || end > offset || split(start) || split(end))
    throw new Error("Use valid UTF-16 boundaries within the text");
  if (spans.some((s) => s.atom && start < s.end && end > s.start))
    throw new Error(
      "Range crosses a paragraph or inline object; use explicit richText replacement",
    );
  if (plain.length - (end - start) + text.length > 12000)
    throw new Error("Text exceeds 12000 UTF-16 units");
  let inserted = false;
  for (const s of spans) {
    if (s.atom) continue;
    const n = s.node;
    if (start === end) {
      if (!inserted && start >= s.start && start <= s.end) {
        n.text =
          n.text!.slice(0, start - s.start) +
          text +
          n.text!.slice(start - s.start);
        inserted = true;
      }
      continue;
    }
    if (s.end <= start || s.start >= end) continue;
    n.text =
      n.text!.slice(0, Math.max(0, start - s.start)) +
      (!inserted ? text : "") +
      n.text!.slice(Math.min(n.text!.length, end - s.start));
    inserted = true;
  }
  if (!inserted && offset === 0 && start === 0) {
    copy.content = [
      { type: "paragraph", content: text ? [{ type: "text", text }] : [] },
    ];
    inserted = true;
  }
  if (!inserted) throw new Error("Range does not address editable text");
  const prune = (n: Node) => {
    n.content = n.content?.filter((c) => c.text !== "");
    n.content?.forEach(prune);
  };
  prune(copy);
  return copy as TLRichText;
}
